import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../apiConfig";
import "../Styles/SuperadminDashboard.css";
import { Building, Users, Plus, Briefcase, MapPin, Mail, Phone, ChevronLeft, ChevronRight, Edit, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Toast from "../Components/Common/Toast";
const SuperadminDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showEditCompanyDialog, setShowEditCompanyDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    companyName: "",
    email: "",
    phone: "",
    address: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage] = useState(5);

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (!token) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("idToken");
        if (!token) {
           showNotification('error', "Unauthorized: Please log in.");
          return;
        }

        // Fetch Companies
        const companiesResponse = await fetch(`${API_BASE_URL}/companies`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (companiesResponse.status === 401 || companiesResponse.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }
        const companiesData = await companiesResponse.json();
        if (companiesData.success) {
          setCompanies(companiesData.companies);
        }

        // Fetch User Summary
        const summaryResponse = await fetch(`${API_BASE_URL}/reports/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
         if (summaryResponse.status === 401 || summaryResponse.status === 403) {
        localStorage.clear();
        window.location.href = '/';
        return;
      }
        const summaryData = await summaryResponse.json();
        if (summaryData.success) {
          setTotalUsers(summaryData.totalUsers);
        }
        
      } catch (err) {
        console.error("Dashboard error:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const showNotification = (type, message) => {
  setNotification({ type, message });
  setTimeout(() => setNotification({ type: '', message: '' }), 5000);
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validate mandatory fields
    const { companyName, email, phone, address, city, state, zip, country } = formData;
    if (!companyName || !email || !address || !city || !state || !zip || !country) {
      setError("Please fill in all mandatory fields.");
      return;
    }
    // Email regex for basic validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    // Phone number: digits only, 7-15 digits (optional field)
    if (phone && !/^\d{7,15}$/.test(phone)) {
      setError("Please enter a valid phone number (7-15 digits, numbers only). Optional field.");
      return;
    }
    // Zip code: digits only, 4-10 digits
    if (!/^\d{4,10}$/.test(zip)) {
      setError("Please enter a valid zip code (4-10 digits).");
      return;
    }
    // No field should be just whitespace
    const fields = [companyName, address, city, state, country];
    if (fields.some(f => !f.trim())) {
      setError("Fields cannot be empty or just spaces.");
      return;
    }
    // Company name length
    if (companyName.length < 2 || companyName.length > 100) {
      setError("Company name must be between 2 and 100 characters.");
      return;
    }
    // Address length
    if (address.length < 5 || address.length > 200) {
      setError("Address must be between 5 and 200 characters.");
      return;
    }
    // City/state/country: no numbers or special chars (basic check)
    const nameRegex = /^[A-Za-z\s\-']+$/;
    if (!nameRegex.test(city) || !nameRegex.test(state) || !nameRegex.test(country)) {
      setError("City, state, and country should only contain letters, spaces, hyphens, or apostrophes.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/companies/superadmin/create-company`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess("Company created successfully!");
        setCompanies((prev) => [...prev, data.company]);
        setFormData({
          companyName: "",
          email: "",
          phone: "",
          address: "",
          street: "",
          city: "",
          state: "",
          zip: "",
          country: "",
        });
      } else {
        setError(data.message || "Failed to create company.");
      }
    } catch (err) {
      console.error("Error creating company:", err);
      setError("An error occurred. Please try again.");
    }
  };

  const handleCompanyClick = (companyId, companyName) => {
    navigate(`/superadmin/company/${companyId}`, {
      state: { companyId, companyName },
    });
  };

  const handleEditCompany = (e, company) => {
    e.stopPropagation(); 
    setEditingCompany(company);
    setFormData({
      companyName: company.companyName || "",
      email: company.email || "",
      phone: company.phone || "",
      address: company.address || "",
      street: company.street || "",
      city: company.city || "",
      state: company.state || "",
      zip: company.zip || "",
      country: company.country || "",
    });
    setShowEditCompanyDialog(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    if (!editingCompany) return;
    const { companyName, email, phone, address, city, state, zip, country } = formData;
    if (!companyName || !email || !address || !city || !state || !zip || !country) {
      setError("Please fill in all mandatory fields.");
      return;
    }
    // Email regex for basic validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    // Phone number: digits only, 7-15 digits (optional field)
    if (phone && !/^\d{7,15}$/.test(phone)) {
      setError("Please enter a valid phone number (7-15 digits, numbers only). Optional field.");
      return;
    }
    // Zip code: digits only, 4-10 digits
    if (!/^\d{4,10}$/.test(zip)) {
      setError("Please enter a valid zip code (4-10 digits).");
      return;
    }
    // No field should be just whitespace
    const fields = [companyName, address, city, state, country];
    if (fields.some(f => !f.trim())) {
      setError("Fields cannot be empty or just spaces.");
      return;
    }
    // Company name length
    if (companyName.length < 2 || companyName.length > 100) {
      setError("Company name must be between 2 and 100 characters.");
      return;
    }
    // Address length
    if (address.length < 5 || address.length > 200) {
      setError("Address must be between 5 and 200 characters.");
      return;
    }
    // City/state/country: no numbers or special chars (basic check)
    const nameRegex = /^[A-Za-z\s\-']+$/;
    if (!nameRegex.test(city) || !nameRegex.test(state) || !nameRegex.test(country)) {
      setError("City, state, and country should only contain letters, spaces, hyphens, or apostrophes.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/companies/${editingCompany.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("idToken")}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess("Company updated successfully!");
        
        setCompanies(companies.map(company => 
          company.id === editingCompany.id ? { ...company, ...formData } : company
        ));
        setShowEditCompanyDialog(false);
        setEditingCompany(null);
        setFormData({
          companyName: "",
          email: "",
          phone: "",
          address: "",
          street: "",
          city: "",
          state: "",
          zip: "",
          country: "",
        });
      } else {
        setError(data.message || "Failed to update company.");
      }
    } catch (err) {
      console.error("Error updating company:", err);
      setError("An error occurred. Please try again.");
    }
  };

const handleDeleteCompany = async (e, companyId) => {
  e.stopPropagation(); 
  setCompanyToDelete(companyId);
  setShowDeleteConfirm(true);
};

const confirmDeleteCompany = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/companies/${companyToDelete}/delete`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("idToken")}`,
      },
    });

    const data = await response.json();
    if (data.success) {
      // Remove company from local state
      setCompanies(companies.filter(company => company.id !== companyToDelete));
      showNotification('success', "Company deleted successfully!");
    } else {
      showNotification('error', data.message || "Failed to delete company.");
    }
  } catch (err) {
    console.error("Error deleting company:", err);
    showNotification('error', "An error occurred while deleting the company.");
  } finally {
    setShowDeleteConfirm(false);
    setCompanyToDelete(null);
  }
};

const cancelDeleteCompany = () => {
  setShowDeleteConfirm(false);
  setCompanyToDelete(null);
};

  const closeEditDialog = () => {
    setShowEditCompanyDialog(false);
    setEditingCompany(null);
    setFormData({
      companyName: "",
      email: "",
      phone: "",
      address: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    });
    setError("");
    setSuccess("");
  };

  // Get current companies for pagination
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = companies.slice(indexOfFirstCompany, indexOfLastCompany);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Calculate total pages
  const totalPages = Math.ceil(companies.length / companiesPerPage);

  return (
    <div className="sadmin-container">
      <div className="ap-wrap-content">
         {/* Add Toast notification */}
     {/* Delete confirmation dialog */}
{showDeleteConfirm && (
  <div className="sadmin-dialog-overlay">
    <div className="sadmin-dialog sadmin-delete-dialog">
      <div className="sadmin-dialog-header">
        <h2>Confirm Delete</h2>
        <button className="sadmin-dialog-close" onClick={cancelDeleteCompany}>
          <X size={20} />
        </button>
      </div>
      <div className="sadmin-dialog-content">
        <p>Are you sure you want to delete this company?</p>
        <p className="sadmin-warning-text">This action cannot be undone.</p>
      </div>
      <div className="sadmin-dialog-buttons">
        <button
          type="button"
          className="sadmin-cancel-button"
          onClick={cancelDeleteCompany}
        >
          Cancel
        </button>
        <button
          type="button"
          className="sadmin-delete-button"
          onClick={confirmDeleteCompany}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
)}

{/* Toast notification */}
<Toast 
  type={notification.type} 
  message={notification.message} 
  onClose={() => setNotification({ type: '', message: '' })} 
/>

      
        <div className="sadmin-header">
          <h1 className="sadmin-header-title">Superadmin Dashboard</h1>
          <button 
            className="sadmin-logout-button" 
            onClick={() => {
              localStorage.clear();
              navigate('/');
            }}
          >
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        <div className="sadmin-stats-grid">
          <div className="sadmin-stat-card">
            <div className="sadmin-icon-container sadmin-icon-purple">
              <Building size={30} color="#6366F1" />
            </div>
            <div>
              <h3 className="sadmin-stat-title">Total Companies</h3>
              <p className="sadmin-stat-value">
                {loading ? "..." : companies.length}
              </p>
            </div>
          </div>

          <div className="sadmin-stat-card-green">
            <div className="sadmin-icon-container sadmin-icon-green">
              <Users size={30} color="#10B981" />
            </div>
            <div>
              <h3 className="sadmin-stat-title-green">Total Users</h3>
              <p className="sadmin-stat-value">
                {loading ? "..." : totalUsers}
              </p>
            </div>
          </div>
        </div>

        <div className="sadmin-content-grid">
          {/* Company Creation Form */}
          <div className="sadmin-card">
            <div className="sadmin-card-header">
              <div className="sadmin-header-icon sadmin-header-icon-purple">
                <Plus size={20} color="white" />
              </div>
              <h2 className="sadmin-card-title">Create a New Company</h2>
            </div>

            {error && (
              <div className="sadmin-alert-error">
                {error}
              </div>
            )}

            {success && (
              <div className="sadmin-alert-success">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="sadmin-form-grid">
                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    Company Name *
                  </label>
                  <div className="sadmin-input-container">
                    <Briefcase size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="sadmin-input-field"
                      placeholder="Enter company name"
                    />
                  </div>
                </div>

                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    Email Address *
                  </label>
                  <div className="sadmin-input-container">
                    <Mail size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="sadmin-input-field"
                      placeholder="company@example.com"
                    />
                  </div>
                </div>

                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    Phone Number (Optional)
                  </label>
                  <div className="sadmin-input-container">
                    <Phone size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="sadmin-input-field"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
              </div>

              <div className="sadmin-form-group">
                <label className="sadmin-form-label">
                  Address *
                </label>
                <div className="sadmin-input-container">
                  <MapPin size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="sadmin-input-field"
                    placeholder="Address line"
                  />
                </div>
              </div>

              <div className="sadmin-address-grid">
                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    Street
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    className="sadmin-form-input"
                    placeholder="Street name"
                  />
                </div>

                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="sadmin-form-input"
                    placeholder="City"
                  />
                </div>

                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    State/Province *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="sadmin-form-input"
                    placeholder="State/Province"
                  />
                </div>

                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    Zip/Postal Code *
                  </label>
                  <input
                    type="text"
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    required
                    className="sadmin-form-input"
                    placeholder="Zip/Postal code"
                  />
                </div>

                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    Country *
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="sadmin-form-input"
                    placeholder="Country"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="sadmin-submit-button"
              >
                <Plus size={18} className="sadmin-button-icon" />
                Create Company
              </button>
            </form>
          </div>

          {/* Company List Section */}
          <div className="sadmin-card">
            <div className="sadmin-card-header">
              <div className="sadmin-header-icon sadmin-header-icon-indigo">
                <Building size={20} color="white" />
              </div>
              <h2 className="sadmin-card-title">Existing Companies</h2>
            </div>

            {loading ? (
              <div className="sadmin-loading-container">
                <div className="sadmin-spinner"></div>
              </div>
            ) : companies.length === 0 ? (
              <div className="sadmin-empty-state">
                <p>No companies available. Create your first company above.</p>
              </div>
            ) : (
              <>
                <div className="sadmin-table-container">
                  <table className="sadmin-table">
                    <thead>
                      <tr>
                        <th className="sadmin-table-header">Company Name</th>
                        <th className="sadmin-table-header">Email</th>
                        <th className="sadmin-table-header">Address</th>
                        <th className="sadmin-table-header">Zip/Postal Code</th>
                        <th className="sadmin-table-header">City</th>
                        <th className="sadmin-table-header">State</th>
                        <th className="sadmin-table-header">Country</th>
                        <th className="sadmin-table-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCompanies.map((company, index) => (
                        <tr
                          key={company.id}
                          className={index % 2 === 0 ? "sadmin-table-row-even" : "sadmin-table-row-odd"}
                        >
                          <td 
                            className="sadmin-table-cell-highlight"
                            onClick={() => handleCompanyClick(company.id, company.companyName)}
                          >
                            {company.companyName}
                          </td>
                          <td 
                            className="sadmin-table-cell"
                            onClick={() => handleCompanyClick(company.id, company.companyName)}
                          >
                            {company.email}
                          </td>
                          <td 
                            className="sadmin-table-cell"
                            onClick={() => handleCompanyClick(company.id, company.companyName)}
                          >
                            {company.address}
                          </td>
                          <td 
                            className="sadmin-table-cell"
                            onClick={() => handleCompanyClick(company.id, company.companyName)}
                          >
                            {company.zip}
                          </td>
                          <td 
                            className="sadmin-table-cell"
                            onClick={() => handleCompanyClick(company.id, company.companyName)}
                          >
                            {company.city}
                          </td>
                          <td 
                            className="sadmin-table-cell"
                            onClick={() => handleCompanyClick(company.id, company.companyName)}
                          >
                            {company.state}
                          </td>
                          <td 
                            className="sadmin-table-cell"
                            onClick={() => handleCompanyClick(company.id, company.companyName)}
                          >
                            {company.country}
                          </td>
                          <td className="sadmin-actions-cell">
                            <div className="sadmin-actions-buttons">
                              <button 
                                className="sadmin-action-btn sadmin-edit-btn" 
                                onClick={(e) => handleEditCompany(e, company)}
                                title="Edit Company"
                              >
                                <Edit size={16} />
                              </button>
                              <button 
                                className="sadmin-action-btn sadmin-delete-btn" 
                                onClick={(e) => handleDeleteCompany(e, company.id)}
                                title="Delete Company"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                      disabled={currentPage === 1}
                      className="pagination-button"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="pagination-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button 
                      onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                      disabled={currentPage === totalPages}
                      className="pagination-button"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Edit Company Dialog */}
        {showEditCompanyDialog && (
          <div className="sadmin-dialog-overlay">
            <div className="sadmin-dialog">
              <div className="sadmin-dialog-header">
                <h2>Edit Company</h2>
                <button className="sadmin-dialog-close" onClick={closeEditDialog}>
                  <X size={20} />
                </button>
              </div>
              
              {error && (
                <div className="sadmin-alert-error">
                  {error}
                </div>
              )}

              {success && (
                <div className="sadmin-alert-success">
                  {success}
                </div>
              )}
              
              <form onSubmit={handleUpdateCompany}>
                <div className="sadmin-form-grid">
                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      Company Name *
                    </label>
                    <div className="sadmin-input-container">
                      <Briefcase size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        className="sadmin-input-field"
                        placeholder="Enter company name"
                      />
                    </div>
                  </div>

                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      Email Address *
                    </label>
                    <div className="sadmin-input-container">
                      <Mail size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="sadmin-input-field"
                        placeholder="company@example.com"
                      />
                    </div>
                  </div>

                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      Phone Number (Optional)
                    </label>
                    <div className="sadmin-input-container">
                      <Phone size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="sadmin-input-field"
                        placeholder="Phone number"
                      />
                    </div>
                  </div>
                </div>

                <div className="sadmin-form-group">
                  <label className="sadmin-form-label">
                    Address *
                  </label>
                  <div className="sadmin-input-container">
                    <MapPin size={18} color="#9ca3af" style={{ marginRight: "8px" }} />
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="sadmin-input-field"
                      placeholder="Address line"
                    />
                  </div>
                </div>

                <div className="sadmin-address-grid">
                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      Street
                    </label>
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      className="sadmin-form-input"
                      placeholder="Street name"
                    />
                  </div>

                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="sadmin-form-input"
                      placeholder="City"
                    />
                  </div>

                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      State/Province *
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="sadmin-form-input"
                      placeholder="State/Province"
                    />
                  </div>

                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      Zip/Postal Code *
                    </label>
                    <input
                      type="text"
                      name="zip"
                      value={formData.zip}
                      onChange={handleChange}
                      required
                      className="sadmin-form-input"
                      placeholder="Zip/Postal code"
                    />
                  </div>

                  <div className="sadmin-form-group">
                    <label className="sadmin-form-label">
                      Country *
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      className="sadmin-form-input"
                      placeholder="Country"
                    />
                  </div>
                </div>

                <div className="sadmin-dialog-buttons">
                  <button
                    type="button"
                    className="sadmin-cancel-button"
                    onClick={closeEditDialog}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="sadmin-submit-button"
                  >
                    Update Company
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperadminDashboard;
